import { uniqueId } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { KeyboardHandler, keyboardManager, KEYBOARD_PRIORITY } from './KeyboardEventManager';

/**
 * 使用键盘事件管理器的Hook
 */
export function useKeyboardManager() {
  const handlersRef = useRef<Set<string>>(new Set());

  /**
   * 注册键盘事件处理器
   */
  const registerHandler = (handler: KeyboardHandler): void => {
    keyboardManager.register(handler);
    handlersRef.current.add(handler.id);
  };

  /**
   * 注销键盘事件处理器
   */
  const unregisterHandler = (id: string): void => {
    keyboardManager.unregister(id);
    handlersRef.current.delete(id);
  };

  /**
   * 批量注册处理器
   */
  const registerHandlers = (handlers: KeyboardHandler[]): void => {
    handlers.forEach((handler) => {
      keyboardManager.register(handler);
      handlersRef.current.add(handler.id);
    });
  };

  /**
   * 批量注销处理器
   */
  const unregisterHandlers = (ids: string[]): void => {
    ids.forEach((id) => {
      keyboardManager.unregister(id);
      handlersRef.current.delete(id);
    });
  };

  /**
   * 获取当前注册的处理器
   */
  const getHandlers = (): KeyboardHandler[] => {
    return keyboardManager.getHandlers();
  };

  // 组件卸载时自动清理注册的处理器
  useEffect(() => {
    const currentHandlers = handlersRef.current;
    return () => {
      currentHandlers.forEach((id) => {
        keyboardManager.unregister(id);
      });
      currentHandlers.clear();
    };
  }, []);

  return {
    registerHandler,
    unregisterHandler,
    registerHandlers,
    unregisterHandlers,
    getHandlers,
  };
}

/**
 * 简化的键盘事件监听Hook
 */
export function useKeyboardHandler(
  keys: string[],
  handler: (event: KeyboardEvent) => boolean | void,
  options: {
    priority?: number;
    eventType?: 'keydown' | 'keyup';
    context?: () => boolean;
    preventDefault?: boolean;
    stopPropagation?: boolean;
  } = {},
) {
  const { registerHandler, unregisterHandler } = useKeyboardManager();
  const handlerIdRef = useRef<string>();

  useEffect(() => {
    const handlerId = uniqueId('handler');
    handlerIdRef.current = handlerId;

    registerHandler({
      id: handlerId,
      keys,
      handler,
      priority: options.priority ?? KEYBOARD_PRIORITY.PAGE,
      eventType: options.eventType ?? 'keydown',
      context: options.context,
      preventDefault: options.preventDefault,
      stopPropagation: options.stopPropagation,
    });

    return () => {
      if (handlerIdRef.current) {
        unregisterHandler(handlerIdRef.current);
      }
    };
  }, [keys, handler, options, registerHandler, unregisterHandler]);
}
